/*
 * Perf.ts
 *
 * Module for tracking the time between two events A and B and emitting the values as analytics and metrics.
 * It can be used to emit single events (if no aggregation is specified) or across different aggregations.
 *
 * These aggregations are useful for capturing per-user performance without emitting a high-cardinality user tag. This
 * is useful for situations like typing performance where the p95 of all typing performances will skew lower,
 * but the p95 of the max of all experiences will give a number that is closer to user "bad" experiences.
 *
 * BenchlingProfiler will be better for most use cases, with more options for what is measured and the output types.
 * https://benchling.atlassian.net/wiki/spaces/PE/pages/233111586/How-to+BenchlingProfiler+-+Measuring+time+between+events
 */

import * as Sentry from '@sentry/browser';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as browser from 'lib/Browser';
import {distribution, count, getMetricsQueue} from 'metrics/Metrics';
import * as logging from 'util/Logging';

let LOG_PERFORMANCE =
  browser.isBrowser &&
  window.performance != null &&
  window.performance.getEntriesByName != null &&
  window.performance.mark != null &&
  window.performance.measure != null &&
  window.performance.clearMeasures != null;

const perfs = new Map<string, Perf>();

interface PerformanceStatistics {
  min_duration: number;
  max_duration: number;
  median_duration: number;
  average_duration: number;
  num_values: number;
}

export enum Aggregation {
  MIN,
  MAX,
  MEDIAN,
  AVERAGE,
  COUNT,
}

export const PERF_ID_DISABLED = Symbol('PerfDisabled');
export type PerfId = string | typeof PERF_ID_DISABLED;

interface Perf {
  intervalId: NodeJS.Timeout | null;
  logPerformance: () => void;
  durationCbs: Array<{(duration: number): void}>;
  span: Sentry.Span | undefined;
  namespace: string;
}

interface AnalyticsOpts {
  enabled: boolean;
  getMetadata?: () => {[key: string]: string | number | boolean};
}

const _calculateStatistics = (entries: PerformanceEntryList): PerformanceStatistics => {
  assert(entries.length > 0, 'expected non-empty entries list when calculating statistics');
  const sortedDurations = _.sortBy(entries.map(({duration}) => duration));
  const middleIndex = Math.floor((sortedDurations.length - 1) / 2);
  return {
    min_duration: sortedDurations[0],
    max_duration: sortedDurations[sortedDurations.length - 1],
    median_duration: sortedDurations[middleIndex],
    average_duration:
      sortedDurations.reduce((result: number, duration: number) => result + duration, 0) /
      sortedDurations.length,
    num_values: entries.length,
  };
};

function sendMetricsWithAggregation(
  namespace: string,
  statistics: PerformanceStatistics,
  aggregationFuncs: Array<Aggregation>,
  metricTags: {[key: string]: string | number | boolean} | {}
): void {
  aggregationFuncs.forEach((fn) => {
    switch (fn) {
      case Aggregation.MIN: {
        distribution(`${namespace}.min`, statistics.min_duration, metricTags);
        break;
      }
      case Aggregation.MAX: {
        distribution(`${namespace}.max`, statistics.max_duration, metricTags);
        break;
      }
      case Aggregation.MEDIAN: {
        distribution(`${namespace}.median`, statistics.median_duration, metricTags);
        break;
      }
      case Aggregation.AVERAGE: {
        distribution(`${namespace}.mean`, statistics.average_duration, metricTags);
        break;
      }
      case Aggregation.COUNT: {
        count(`${namespace}.count`, statistics.num_values, metricTags);
        break;
      }
      default:
        assert(false, `Unexpected aggregation func value ${fn}`);
    }
  });
}

const _handleError = (e: Error, perfId: string): void => {
  LOG_PERFORMANCE = false;
  logging.error(e);
  const perf = perfs.get(perfId)!;
  if (perf.intervalId != null) {
    clearInterval(perf.intervalId);
  }
  perfs.delete(perfId);
};

const _logPerformance = (
  namespace: string,
  perfId: string,
  analyticsOpts: AnalyticsOpts,
  metricTags: {[key: string]: string | number | boolean} | {} = {}
): void => {
  try {
    const entries = window.performance.getEntriesByName(perfId);
    window.performance.clearMeasures(perfId);
    entries.forEach((entry) => {
      distribution(_metricName(namespace), entry.duration, metricTags);
    });
    if (entries.length > 0) {
      _sendGranularAnalytics(namespace, analyticsOpts);
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    _handleError(e, perfId);
  }
};

const _logPerformanceWithAggregation = (
  namespace: string,
  perfId: string,
  aggregationFuncs: Array<Aggregation>,
  analyticsOpts: AnalyticsOpts,
  metricTags: {[key: string]: string | number | boolean} | {} = {}
): void => {
  try {
    const entries = window.performance.getEntriesByName(perfId);
    window.performance.clearMeasures(perfId);
    if (entries.length > 0) {
      const statistics = _calculateStatistics(entries);
      _sendGranularAnalytics(namespace, analyticsOpts, statistics);
      sendMetricsWithAggregation(_metricName(namespace), statistics, aggregationFuncs, metricTags);
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    _handleError(e, perfId);
  }
};

function _sendGranularAnalytics(
  namespace: string,
  analyticsOpts: AnalyticsOpts,
  statistics?: PerformanceStatistics
): void {
  if (!analyticsOpts.enabled || !analyticsOpts.getMetadata) {
    return;
  }
  const metadata = {revision: window.REVISION, namespace, ...analyticsOpts.getMetadata(), ...statistics};
  analytics.track('perf', 'tracking', metadata);
}

function _metricName(namespace: string): string {
  return `perf.${namespace}`;
}

// Expose for testing
const _testing = {
  _calculateStatistics,
  _logPerformance,
  _logPerformanceWithAggregation,
};
export {_testing};

/*
 * Starts tracking performance for a namespace. Perf uses the Window Performance API to measure the
 * performance of functions. Perf will collect all measures every @aggregation.interval and log the slowest
 * duration through the analytics perf module.
 * @param  {String}   namespace     namespace of the event for analytics tracking, "perf.${namespace}"
 * @param  {Object}   analyticsOpts interface to enable snowflake analytics and pass metadata
 * @param  {Object}   aggregation   how often to parse measures and log data
 * @param  {Object}   metricTags    the tags that will be set for this measure when it is sent to DataDog
 * @return {Number}   perfId        used to create measures and stop Perf for this namespace
 */
export function start(
  namespace: string,
  analyticsOpts: AnalyticsOpts,
  aggregation?: {interval: number; aggregations: Array<Aggregation>},
  metricTags?: {[key: string]: string | number | boolean} | {},
  durationCbs: Array<{(duration: number): void}> = []
): PerfId {
  if (!LOG_PERFORMANCE) {
    return PERF_ID_DISABLED;
  }
  const perfId = _.uniqueId(`${namespace}_`);
  metricTags = metricTags || {};
  const logPerformance =
    aggregation === undefined
      ? _logPerformance.bind(null, namespace, perfId, analyticsOpts, metricTags)
      : _logPerformanceWithAggregation.bind(
          null,
          namespace,
          perfId,
          aggregation.aggregations,
          analyticsOpts,
          metricTags
        );
  const intervalId = aggregation === undefined ? null : setInterval(logPerformance, aggregation.interval);
  perfs.set(perfId, {
    intervalId,
    logPerformance,
    durationCbs,
    namespace,
    span: undefined,
  });
  // Mainly for performance testing to log remaining data before the page is closed.
  // Only if we are running in Cypress. Otherwise it can cause a popup
  // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (window.Cypress) {
    window.addEventListener('beforeunload', (event) => {
      logPerformance();
      getMetricsQueue()._flush();
    });
  }
  return perfId;
}

/**
 * Creates a timestamp in the browser's performance entry buffer with the given name.
 *
 * @param perfId - a unique name to track some measurement
 */
export function markStart(perfId: PerfId): void {
  if (perfId === PERF_ID_DISABLED || !LOG_PERFORMANCE || !perfs.has(perfId)) {
    return;
  }
  try {
    const perf = perfs.get(perfId)!;
    const perfEventSampleRate =
      BenchlingConfig.PERF_ENABLE_JS_SELF_SAMPLING_PROFILER?.perfEventSampleRates[perf.namespace];
    // Create a trace for the perf event according to the rate specified in the config (E.g. if the rate in
    // config, is 0.5, we would trace this event 50% of the time.
    if (perfEventSampleRate && Math.random() < perfEventSampleRate) {
      Sentry.startSpanManual({name: perf.namespace}, (span) => {
        perf.span = span;
      });
    }
    window.performance.mark(`${perfId}-start`);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    _handleError(e, perfId);
  }
}

/**
 * Measures the time between when the corresponding `markStart` and this was called, queuing the metric
 * to be logged.
 *
 * @param perfId - a unique name to track some measurement
 */
export function markEnd(perfId: PerfId): void {
  if (perfId === PERF_ID_DISABLED || !LOG_PERFORMANCE || !perfs.has(perfId)) {
    return;
  }

  const perf = perfs.get(perfId)!;

  perf.span?.end();

  try {
    window.performance.mark(`${perfId}-end`);
    // If more than one mark with this name exists, the most recent one is used.
    window.performance.measure(`${perfId}`, `${perfId}-start`, `${perfId}-end`);
    callDurationCbs(perf, perfId);
    if (perf.intervalId == null) {
      perf.logPerformance();
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    _handleError(e, perfId);
  }
}

function callDurationCbs(perf: Perf, perfId: string): void {
  if (!perf.durationCbs || perf.durationCbs.length === 0) {
    return;
  }
  const measures = window.performance.getEntriesByName(`${perfId}`, 'measure');
  if (!measures || measures.length === 0) {
    return;
  }
  const measure = measures[measures.length - 1] as PerformanceMeasure | null;
  if (measure?.duration) {
    for (const func of perf.durationCbs) {
      func(measure.duration);
    }
  }
}

/*
 * Creates a measure for a Perf by marking the start time and end time of the function call.
 */
export function measure(perfId: PerfId): void {
  if (perfId === PERF_ID_DISABLED || !LOG_PERFORMANCE || !perfs.has(perfId)) {
    return;
  }
  try {
    // Create a measure for handling the input for when the handler is fired and when it completes
    markStart(perfId);
    _.defer(() => {
      markEnd(perfId);
    });
    // Performance.mark will throw a SyntaxError if the namespace already exists, i.e. as part of the
    // PerformanceTiming interface.
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    _handleError(e, perfId);
  }
}

/*
 * Creates a measure for a Perf by wrapping the passed function
 */
export function measureFn<T>(perfId: PerfId, fn: () => T): T {
  if (perfId === PERF_ID_DISABLED || !LOG_PERFORMANCE || !perfs.has(perfId)) {
    return fn();
  }
  markStart(perfId);
  const ret = fn();
  markEnd(perfId);

  return ret;
}

/*
 * Stops measuring performance for a Perf.
 */
export function stop(perfId: PerfId): void {
  if (perfId === PERF_ID_DISABLED || !LOG_PERFORMANCE || !perfs.has(perfId)) {
    return;
  }
  // Log performance for any remaining measures in the interval.
  const perf = perfs.get(perfId)!;
  perf.logPerformance();
  if (perf.intervalId != null) {
    clearInterval(perf.intervalId);
  }
  perfs.delete(perfId);
}
